import React, { useState } from "react";
import MobileStaffLayout from "../../components/mobile-staff/MobileStaffLayout";
import api from "../../api/client";
import { fmtXOF, fmtDate, NATURES_ECHEANCE } from "../../lib/format";
import { Search, Loader2, CheckCircle2, Calculator, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const NATURES = [["VERSEMENT_AVANCE", "Avance / apport"], ["MENSUALITE", "Mensualité"], ["FRAIS", "Frais"], ["SOLDE_FINAL", "Solde final"], ["AUTRE", "Autre"]];
const MODES = [["especes", "Espèces"], ["virement", "Virement"], ["wave", "Wave"], ["orange_money", "Orange Money"], ["cheque", "Chèque"], ["carte", "Carte"]];
const inp = "w-full h-12 px-4 rounded-md border border-[#E2E5F1] bg-white text-sm focus:outline-none focus:border-[#1E3A8A]";
const lbl = "text-[10px] uppercase tracking-wider font-bold text-[#4B5775] block mb-1.5";
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function Resultats({ results, onPick }) {
  return results.map((r) => (
    <button key={r.contrat.id} type="button" onClick={() => onPick(r)} className="w-full text-left p-4 mb-2 rounded-xl bg-white border border-[#E2E5F1]" data-testid={`ms-enc-result-${r.contrat.id}`}>
      <div className="font-bold text-[#0F1A3D]">{r.client?.raison_sociale || `${r.client?.prenom || ""} ${r.client?.nom || ""}`}</div>
      <div className="text-xs text-[#4B5775]">{r.unite?.nom} · {r.contrat.reference} · solde exigible {fmtXOF(r.stats.solde_confirme)}{r.stats.montant_a_rapprocher > 0 ? ` · à rapprocher ${fmtXOF(r.stats.montant_a_rapprocher)}` : ""}</div>
    </button>
  ));
}

function Ventilation({ prop }) {
  return (
    <div className="p-3 rounded-lg bg-[#F1F3FB] text-xs" data-testid="ms-enc-ventilation">
      <div className={lbl}>Ventilation proposée</div>
      {prop.lignes.length === 0 && <div className="text-[#C83B3B]">Aucune échéance impayée de cette nature.</div>}
      {prop.lignes.map((l) => (
        <div key={l.paiement_id} className="flex justify-between py-1"><span>{NATURES_ECHEANCE[l.nature]} n°{l.numero_nature || l.numero} · {fmtDate(l.date_echeance)}</span><b>{fmtXOF(l.montant)}</b></div>
      ))}
      {prop.surplus > 0 && <div className="mt-2 text-[#8B4E12]"><AlertTriangle className="w-3 h-3 inline" /> Surplus {fmtXOF(prop.surplus)} : jamais basculé sur une autre nature.</div>}
    </div>
  );
}

export default function StaffEncaissement() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ nature: "", montant: "", mode: "especes", reference: "" });
  const [prop, setProp] = useState(null);
  const [credit, setCredit] = useState(false);
  const [key, setKey] = useState(newKey());
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); if (k === "nature" || k === "montant") setProp(null); };

  const search = async (text) => {
    setQ(text);
    if (text.length < 2) { setResults([]); return; }
    const { data } = await api.get("/paiements/search/quick", { params: { q: text } });
    setResults(data?.results || []);
  };
  const pick = (r) => {
    setSel(r); setResults([]); setProp(null);
    const apportDu = r.paiements.some((p) => ["VERSEMENT_AVANCE", "APPORT_INITIAL"].includes(p.nature) && p.statut !== "a_rapprocher" && (p.montant_recu || 0) < p.montant && p.statut !== "annule");
    setF((x) => ({ ...x, nature: apportDu ? "VERSEMENT_AVANCE" : "" }));
  };
  const proposer = async () => {
    try {
      const { data } = await api.post("/encaissements/proposer", { contrat_id: sel.contrat.id, nature: f.nature, montant: Math.round(Number(f.montant)) });
      setProp(data); setCredit(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const valider = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/encaissements", {
        contrat_id: sel.contrat.id, nature: f.nature, montant: Math.round(Number(f.montant)), mode: f.mode, reference: f.reference,
        date_paiement: new Date().toISOString().slice(0, 10), confirmer_credit: credit, idempotency_key: key, canal: "mobile_staff",
        lignes: prop.lignes.map((l) => ({ paiement_id: l.paiement_id, montant: l.montant })),
      });
      toast.success(`${fmtXOF(data.montant)} encaissé · reçu ${data.recu_numero}`);
      setSel(null); setProp(null); setQ(""); setF({ nature: "", montant: "", mode: "especes", reference: "" }); setKey(newKey());
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  return (
    <MobileStaffLayout title="Encaissement">
      <div className="px-5 pt-5 pb-24 space-y-4" data-testid="ms-enc-form">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-1">// ENCAISSEMENT</div>
          <p className="text-xs text-[#4B5775]">Choisissez la nature, vérifiez la ventilation, puis confirmez. Un versement d'avance n'est jamais une mensualité.</p>
        </div>
        {!sel && (
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-4 text-[#4B5775]" />
            <input value={q} onChange={(e) => search(e.target.value)} placeholder="Nom, téléphone, référence contrat" className={`${inp} pl-9`} data-testid="ms-enc-search" />
            <div className="mt-3"><Resultats results={results} onPick={pick} /></div>
          </div>
        )}
        {sel && (<>
          <div className="p-4 rounded-xl bg-[#0F1A3D] text-white">
            <div className="font-bold">{sel.contrat.reference} · {sel.unite?.nom}</div>
            <button type="button" onClick={() => setSel(null)} className="text-[10px] uppercase font-bold text-white/70 mt-1" data-testid="ms-enc-change">Changer</button>
          </div>
          <div>
            <label className={lbl}>Ce paiement règle (obligatoire)</label>
            <div className="grid grid-cols-2 gap-2">
              {NATURES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("nature", v)} className={`h-10 rounded-md text-xs font-bold ${f.nature === v ? "bg-[#E91E63] text-white" : "bg-white border border-[#E2E5F1] text-[#4B5775]"}`} data-testid={`ms-enc-nature-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div><label className={lbl}>Montant (FCFA)</label>
            <input type="number" step="1" min="1" value={f.montant} onChange={(e) => set("montant", e.target.value)} className={`${inp} text-lg font-bold`} data-testid="ms-enc-amount" /></div>
          <div className="grid grid-cols-3 gap-2">
            {MODES.map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("mode", v)} className={`h-10 rounded-md text-[11px] font-bold ${f.mode === v ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E5F1] text-[#4B5775]"}`} data-testid={`ms-enc-mode-${v}`}>{l}</button>
            ))}
          </div>
          <input value={f.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Référence / preuve" className={inp} data-testid="ms-enc-reference" />
          <button type="button" disabled={!f.nature || !(Number(f.montant) > 0)} onClick={proposer} className="w-full h-12 rounded-md bg-[#F1F3FB] font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ms-enc-proposer">
            <Calculator className="w-4 h-4" /> Proposer la ventilation
          </button>
          {prop && (<>
            <Ventilation prop={prop} />
            {prop.surplus > 0 && (
              <label className="flex gap-2 text-xs"><input type="checkbox" checked={credit} onChange={(e) => setCredit(e.target.checked)} data-testid="ms-enc-credit" /> Enregistrer {fmtXOF(prop.surplus)} comme crédit client non imputé</label>
            )}
            <button type="button" onClick={valider} disabled={busy || (prop.surplus > 0 && !credit)} className="w-full h-14 rounded-md bg-[#E91E63] text-white font-bold uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ms-enc-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmer l'encaissement
            </button>
          </>)}
        </>)}
      </div>
    </MobileStaffLayout>
  );
}
